import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { useCurrency } from '../hooks/useCurrency';
import { bookingService, BookingRequest, RoomAvailability } from '../services/booking';

interface BookingFormProps {
    hotelId: string;
    onBookingSuccess?: (bookingId: string, confirmationNumber: string) => void;
}

const BookingForm: React.FC<BookingFormProps> = ({ hotelId, onBookingSuccess }) => {
    const { t } = useTranslation();
    const { formatPrice } = useCurrency();
    
    const [step, setStep] = useState<'search' | 'rooms' | 'details' | 'confirmation'>('search');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    
    // Search form state
    const [searchData, setSearchData] = useState({
        checkIn: '',
        checkOut: '',
        guests: 1
    });
    
    // Room selection state
    const [availableRooms, setAvailableRooms] = useState<RoomAvailability[]>([]);
    const [selectedRoom, setSelectedRoom] = useState<string>('');
    
    // Guest details state
    const [guestInfo, setGuestInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        specialRequests: ''
    });
    
    // Booking result state
    const [bookingResult, setBookingResult] = useState<{
        bookingId: string;
        confirmationNumber: string;
        totalAmount: number;
    } | null>(null);

    const handleSearch = async () => {
        if (!searchData.checkIn || !searchData.checkOut) {
            setError(t('booking.selectDates'));
            return;
        }

        if (new Date(searchData.checkIn) >= new Date(searchData.checkOut)) {
            setError(t('booking.invalidDates'));
            return;
        }

        setLoading(true);
        setError('');
        setMessage('');

        try {
            const rooms = await bookingService.checkAvailability({
                hotelId,
                checkIn: searchData.checkIn,
                checkOut: searchData.checkOut,
                guests: searchData.guests
            });

            setAvailableRooms(rooms);
            setStep('rooms');
        } catch (err) {
            setError(t('booking.searchError'));
        } finally {
            setLoading(false);
        }
    };

    const handleRoomSelect = (roomId: string) => {
        setSelectedRoom(roomId);
        setStep('details');
    };

    const handleGuestInfoChange = (field: string, value: string) => {
        setGuestInfo(prev => ({ ...prev, [field]: value }));
    };

    const handleBookingSubmit = async () => {
        if (!guestInfo.firstName || !guestInfo.lastName || !guestInfo.email) {
            setError(t('booking.fillRequiredFields'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const bookingRequest: BookingRequest = {
                hotelId,
                roomId: selectedRoom,
                checkIn: searchData.checkIn,
                checkOut: searchData.checkOut,
                guests: searchData.guests,
                guestInfo
            };

            const result = await bookingService.createBooking(bookingRequest);

            if (result.success && result.bookingId && result.confirmationNumber && result.totalAmount) {
                setBookingResult({
                    bookingId: result.bookingId,
                    confirmationNumber: result.confirmationNumber,
                    totalAmount: result.totalAmount
                });
                setStep('confirmation');
                onBookingSuccess?.(result.bookingId, result.confirmationNumber);
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError(t('booking.bookingError'));
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setStep('search');
        setSearchData({ checkIn: '', checkOut: '', guests: 1 });
        setSelectedRoom('');
        setGuestInfo({ firstName: '', lastName: '', email: '', phone: '', specialRequests: '' });
        setBookingResult(null);
        setError('');
        setMessage('');
    };

    const getSelectedRoomPrice = () => {
        const room = availableRooms.find(r => r.roomId === selectedRoom);
        return room?.price || 0;
    };

    const calculateTotal = () => {
        if (!searchData.checkIn || !searchData.checkOut) return 0;
        
        const checkInDate = new Date(searchData.checkIn);
        const checkOutDate = new Date(searchData.checkOut);
        const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
        
        return getSelectedRoomPrice() * nights;
    };

    return (
        <div className="booking-form-container">
            <div className="booking-form-header">
                <h3>{t('booking.title')}</h3>
                {step !== 'search' && (
                    <button 
                        className="btn btn-secondary btn-sm"
                        onClick={resetForm}
                    >
                        {t('booking.newSearch')}
                    </button>
                )}
            </div>

            {error && (
                <motion.div 
                    className="alert alert-error"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    {error}
                </motion.div>
            )}

            {message && (
                <motion.div 
                    className="alert alert-success"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    {message}
                </motion.div>
            )}

            {/* Step 1: Search */}
            {step === 'search' && (
                <motion.div 
                    className="booking-step"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                >
                    <div className="form-group">
                        <label>{t('hotelDetails.checkIn')}</label>
                        <input
                            type="date"
                            value={searchData.checkIn}
                            onChange={(e) => setSearchData(prev => ({ ...prev, checkIn: e.target.value }))}
                            min={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>{t('hotelDetails.checkOut')}</label>
                        <input
                            type="date"
                            value={searchData.checkOut}
                            onChange={(e) => setSearchData(prev => ({ ...prev, checkOut: e.target.value }))}
                            min={searchData.checkIn || new Date().toISOString().split('T')[0]}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>{t('hotelDetails.guests')}</label>
                        <select
                            value={searchData.guests}
                            onChange={(e) => setSearchData(prev => ({ ...prev, guests: parseInt(e.target.value) }))}
                        >
                            {[1, 2, 3, 4, 5, 6].map(num => (
                                <option key={num} value={num}>
                                    {num} {num === 1 ? t('booking.guest') : t('booking.guests')}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <button 
                        className="btn btn-primary btn-large"
                        onClick={handleSearch}
                        disabled={loading}
                    >
                        {loading ? t('booking.searching') : t('hotelDetails.checkAvailability')}
                    </button>
                </motion.div>
            )}

            {/* Step 2: Room Selection */}
            {step === 'rooms' && (
                <motion.div 
                    className="booking-step"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                >
                    <h4>{t('booking.selectRoom')}</h4>
                    <div className="rooms-list">
                        {availableRooms.map((room) => (
                            <div 
                                key={room.roomId} 
                                className={`room-option ${selectedRoom === room.roomId ? 'selected' : ''} ${!room.available ? 'unavailable' : ''}`}
                                onClick={() => room.available && handleRoomSelect(room.roomId)}
                            >
                                <div className="room-info">
                                    <h5>{t(`booking.roomType${room.roomId}`)}</h5>
                                    <p>{t(`booking.roomDescription${room.roomId}`)}</p>
                                    <div className="room-availability">
                                        {room.available ? (
                                            <span className="available">
                                                {room.availableRooms} {t('booking.roomsAvailable')}
                                            </span>
                                        ) : (
                                            <span className="unavailable">{t('booking.soldOut')}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="room-price">
                                    <span className="price">{formatPrice(room.price)}</span>
                                    <span className="period">{t('hotelDetails.perNight')}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {selectedRoom && (
                        <motion.div 
                            className="booking-summary"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <h5>{t('booking.bookingSummary')}</h5>
                            <div className="summary-details">
                                <div className="summary-item">
                                    <span>{t('booking.dates')}:</span>
                                    <span>{searchData.checkIn} - {searchData.checkOut}</span>
                                </div>
                                <div className="summary-item">
                                    <span>{t('booking.guests')}:</span>
                                    <span>{searchData.guests}</span>
                                </div>
                                <div className="summary-item total">
                                    <span>{t('booking.total')}:</span>
                                    <span>{formatPrice(calculateTotal())}</span>
                                </div>
                            </div>
                            <button 
                                className="btn btn-primary"
                                onClick={() => setStep('details')}
                            >
                                {t('booking.continue')}
                            </button>
                        </motion.div>
                    )}
                </motion.div>
            )}

            {/* Step 3: Guest Details */}
            {step === 'details' && (
                <motion.div 
                    className="booking-step"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                >
                    <h4>{t('booking.guestDetails')}</h4>
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label>{t('booking.firstName')} *</label>
                            <input
                                type="text"
                                value={guestInfo.firstName}
                                onChange={(e) => handleGuestInfoChange('firstName', e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('booking.lastName')} *</label>
                            <input
                                type="text"
                                value={guestInfo.lastName}
                                onChange={(e) => handleGuestInfoChange('lastName', e.target.value)}
                                required
                            />
                        </div>
                    </div>
                    
                    <div className="form-group">
                        <label>{t('booking.email')} *</label>
                        <input
                            type="email"
                            value={guestInfo.email}
                            onChange={(e) => handleGuestInfoChange('email', e.target.value)}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>{t('booking.phone')}</label>
                        <input
                            type="tel"
                            value={guestInfo.phone}
                            onChange={(e) => handleGuestInfoChange('phone', e.target.value)}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>{t('booking.specialRequests')}</label>
                        <textarea
                            value={guestInfo.specialRequests}
                            onChange={(e) => handleGuestInfoChange('specialRequests', e.target.value)}
                            rows={3}
                            placeholder={t('booking.specialRequestsPlaceholder')}
                        />
                    </div>
                    
                    <button 
                        className="btn btn-primary btn-large"
                        onClick={handleBookingSubmit}
                        disabled={loading}
                    >
                        {loading ? t('booking.processing') : t('booking.confirmBooking')}
                    </button>
                </motion.div>
            )}

            {/* Step 4: Confirmation */}
            {step === 'confirmation' && bookingResult && (
                <motion.div 
                    className="booking-step confirmation"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                >
                    <div className="confirmation-icon">✅</div>
                    <h4>{t('booking.bookingConfirmed')}</h4>
                    <p>{t('booking.confirmationMessage')}</p>
                    
                    <div className="confirmation-details">
                        <div className="confirmation-item">
                            <span>{t('booking.confirmationNumber')}:</span>
                            <strong>{bookingResult.confirmationNumber}</strong>
                        </div>
                        <div className="confirmation-item">
                            <span>{t('booking.bookingId')}:</span>
                            <strong>{bookingResult.bookingId}</strong>
                        </div>
                        <div className="confirmation-item">
                            <span>{t('booking.totalPaid')}:</span>
                            <strong>{formatPrice(bookingResult.totalAmount)}</strong>
                        </div>
                    </div>
                    
                    <div className="confirmation-actions">
                        <button 
                            className="btn btn-primary"
                            onClick={() => window.print()}
                        >
                            {t('booking.printConfirmation')}
                        </button>
                        <button 
                            className="btn btn-secondary"
                            onClick={resetForm}
                        >
                            {t('booking.newBooking')}
                        </button>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default BookingForm;

